// src/audio/LockScreenControls.js
//
// Thin wrapper around react-native-track-player (RNTP) that provides
// lock-screen media controls on iOS (Control Center / Now Playing) and
// Android (media notification in the notification shade) without conflicting
// with expo-av's multi-layer audio playback.
//
// Architecture
// ────────────
// expo-av handles all actual audio loading, mixing, and volume control.
// RNTP is used solely as a media-session surface:
//
//   iOS  — registers MPRemoteCommandCenter handlers so the OS forwards
//           lock-screen / Control Center play ▶ pause ⏸ stop ⏹ taps to
//           AudioEngine. MPNowPlayingInfoCenter is updated with the active mix
//           title so the lock-screen card always shows the correct name.
//
//   Android — starts a foreground MediaSession service that displays a
//              persistent media notification with play/pause/stop controls.
//
// RNTP needs at least one track loaded AND in a Playing state to display the
// Android notification and keep iOS lock-screen controls active. We load a
// bundled 1-second silent WAV that loops forever (RepeatMode.Track) so RNTP
// stays in a "playing" state indefinitely — it is inaudible since expo-av owns
// the real audio output.
//
// Remote-command handlers (RemotePlay, RemotePause, RemoteStop) are registered
// in the APP JS context inside init() — not in the RNTP background service —
// so they share the exact same AudioEngine singleton and expo-av sound objects
// that are managing playback. The PlaybackService (TrackPlayerService.js) is a
// required no-op stub that satisfies RNTP's registration requirement.

import { Platform, Image } from "react-native";
import TrackPlayer, {
  Capability,
  Event,
  RepeatMode,
  State,
} from "react-native-track-player";
import { AudioEngine } from "./AudioEngine";

// Built lazily inside initLockScreenControls() so Image.resolveAssetSource
// is never called on web (it doesn't exist in react-native-web and would
// crash the module at import time before any Platform guard runs).
let SILENT_TRACK = null;
function getSilentTrack() {
  if (!SILENT_TRACK) {
    SILENT_TRACK = {
      id: "sleepmix-session",
      url: require("../../assets/silence.wav"),
      title: "SleepMixBlueprint",
      artist: "SleepMixBlueprint",
      album: "Ambient Sleep Sounds",
      artwork: Image.resolveAssetSource(require("../../assets/icon.png"))?.uri,
      duration: 1,
    };
  }
  return SILENT_TRACK;
}

let _initialized = false;
let _lastMirroredIsPlaying = null;

// Call once, after AudioEngine.init(), before any audio starts playing.
// Registers remote-command handlers in the app JS context so they share
// the live AudioEngine singleton.
export async function initLockScreenControls() {
  if (Platform.OS === "web") return; // handled by MediaSessionManager (navigator.mediaSession)
  if (_initialized) return;

  // Track whether setupPlayer succeeded or was already initialised so we know
  // whether to re-add the silent track later in this function.
  let playerAlreadyReady = false;

  try {
    await TrackPlayer.setupPlayer({
      // AudioEngine already manages audio-focus / interruptions via expo-av;
      // disable RNTP's own interruption handling to avoid double-handling.
      autoHandleInterruptions: false,
    });
  } catch (e) {
    // On Android, force-closing the app kills the JS context but can leave the
    // RNTP foreground service (and its MediaSession) alive. When the JS context
    // restarts, setupPlayer() throws because the native player is already
    // initialised. Detect this and continue — we still need to re-register the
    // remote-command handlers and refresh the Now Playing metadata.
    const msg = e?.message ?? String(e);
    if (
      msg.includes("already") || // RNTP v4: "The player has already been initialized"
      msg.includes("initialized") ||
      msg.includes("initialised")
    ) {
      if (__DEV__) {
        console.log(
          "[LockScreenControls] player already initialised (Android relaunch) — skipping setupPlayer, re-registering handlers.",
        );
      }
      playerAlreadyReady = true;
    } else {
      // Unexpected error — lock-screen controls unavailable, but audio still works.
      console.warn("[LockScreenControls] setupPlayer failed:", msg);
      return;
    }
  }

  try {
    await TrackPlayer.updateOptions({
      // Buttons shown on the lock screen and in the Android notification.
      capabilities: [Capability.Play, Capability.Pause, Capability.Stop],
      // Compact Android notification shows only these two.
      compactCapabilities: [Capability.Play, Capability.Pause],
    });

    // ── Remote-command handlers — wired in the APP JS context ─────────────
    // Registering here (not in PlaybackService) ensures we operate on the
    // exact AudioEngine instance that owns the live expo-av sound objects.
    // On Android relaunch these must be re-registered because the JS context
    // was destroyed (the previous listeners are gone) even though the native
    // RNTP service survived.

    TrackPlayer.addEventListener(Event.RemotePlay, () => {
      // Lock-screen ▶ → resume all paused AudioEngine layers.
      AudioEngine.resumeAll();
    });

    TrackPlayer.addEventListener(Event.RemotePause, () => {
      // Lock-screen ⏸ → pause all active AudioEngine layers.
      AudioEngine.pauseAll();
    });

    TrackPlayer.addEventListener(Event.RemoteStop, () => {
      // Lock-screen ⏹ → fade out and unload all layers.
      AudioEngine.stopAll({ fadeMs: 2000 });
    });

    // ── Silent placeholder track ───────────────────────────────────────────
    // Load the 1 s silent WAV and set it to loop forever so RNTP remains in
    // a Playing state for the entire session. This is required for:
    //   • Android: foreground MediaSession service shows the notification only
    //     while a track is loaded and the player is playing.
    //   • iOS: MPRemoteCommandCenter controls remain registered and visible on
    //     the lock screen as long as there is an active Now Playing session.
    //
    // On Android relaunch the native service kept the track queue intact, so
    // skip add() to avoid a duplicate-track error. Re-apply RepeatMode in
    // both paths: on first setup it must be set; on relaunch the native layer
    // may have reset it when the JS context was destroyed.
    if (!playerAlreadyReady) {
      await TrackPlayer.add(getSilentTrack());
    }
    await TrackPlayer.setRepeatMode(RepeatMode.Track); // loop indefinitely
    await TrackPlayer.play(); // (re-)start / confirm RNTP's media session

    _initialized = true;
  } catch (e) {
    // Non-fatal — lock-screen controls unavailable, but audio still works.
    console.warn("[LockScreenControls] setup failed:", e?.message ?? e);
  }
}

// Update the Now Playing title, artwork, and mirror the play/pause state into
// RNTP so the lock-screen card shows accurate information.
// Safe to call before init() completes — it no-ops gracefully.
// `artworkUri` is an optional resolved URI string (use Image.resolveAssetSource).
export async function updateLockScreen({ title, isPlaying, artworkUri }) {
  if (!_initialized || Platform.OS === "web") return;

  try {
    // Update the Now Playing card (title shown on iOS lock screen /
    // Control Center and as the Android notification title; artwork shown
    // as the album art thumbnail).
    const meta = {
      title: title || "SleepMixBlueprint",
      artist: "SleepMixBlueprint",
      album: "Ambient Sleep Sounds",
    };
    if (artworkUri) {
      meta.artwork = artworkUri;
    } else if (__DEV__) {
      console.warn(
        "[LockScreenControls] updateLockScreen called without artworkUri — lock screen artwork will not update.",
      );
    }

    if (__DEV__) {
      console.log("[LockScreenControls] updateNowPlayingMetadata →", {
        title: meta.title,
        artwork: meta.artwork ?? "(none)",
      });
    }

    await TrackPlayer.updateNowPlayingMetadata(meta);

    // Mirror AudioEngine's play/pause into RNTP so the lock-screen button
    // icon stays correct. RepeatMode.Track keeps the track looping, so a
    // TrackPlayer.play() call after pause will resume the looping session
    // rather than advancing to a non-existent next track.
    // TEMP TEST: disabled to check if RNTP's play/pause calls are causing
    // an audio-focus fight with the real sound. Re-enable by uncommenting.
    // if (isPlaying !== _lastMirroredIsPlaying) {
    //   const { state } = await TrackPlayer.getPlaybackState();
    //   if (isPlaying && state !== State.Playing) {
    //     await TrackPlayer.play();
    //   } else if (!isPlaying && state === State.Playing) {
    //     await TrackPlayer.pause();
    //   }
    //   _lastMirroredIsPlaying = isPlaying;
    // }
  } catch (e) {
    // Non-critical — metadata sync failure doesn't affect actual audio.
    if (__DEV__)
      console.warn(
        "[LockScreenControls] updateNowPlayingMetadata failed:",
        e?.message ?? e,
      );
  }
}
